import { ApiService } from '../apiService'
import type { AdventureInterestState, InterestAvailability } from '../types/interest'
import {
  AdventureDto,
  AdventureListResponse,
  AdventureListParams,
  CreateAdventureRequest,
  UpdateAdventureRequest,
  SavedSearchFilterDto,
  CreateSavedSearchFilterRequest,
  AdventureSearchRequest,
  AdventureCategoryDto,
  AdventureJoinRequest,
  AdventureJoinResponse,
  CriteriaTypeDto,
  NodeTypeDto,
  JoinEligibilityResult,
  CompletedAdventuresResponse,
  AdventureChildDto,
  TrendingAdventuresResponse,
} from '../types/adventure'
import { PublicQueueSessionDto, ModeratorStatusDto, type StatsCountStatus } from '../types/session'
import { ApiResponse } from '../types/common'
import { AdventureLeaderboardResponse, LeaderboardQueryParams } from '../types/leaderboard'
import {
  AdventureMaintainerDto,
  AdventureModeratorDto,
  AdventureTesterDto,
  AddTeamMemberRequest,
  UnlockRequirement,
} from '@features/adventures/types/adventure-editor.types'

// Criteria request/response types
export interface AddCriterionRequest {
  conditionType: number
  config: Record<string, unknown>
  logicGroupId: string // Required - must belong to a logic group
  order?: number
}

export interface UpdateCriterionRequest {
  conditionType?: number
  config?: Record<string, unknown>
  logicGroupId?: string
  order?: number
}

export interface CriterionResponse {
  id: string
  conditionType: number
  description?: string
  isOptional?: boolean
  config: Record<string, unknown>
  priority: number // API uses "priority" for ordering
}

// Logic Group types - API uses nested response format
export interface CriteriaGroupDto {
  id: string
  isAnd: boolean
  orderIndex: number
  criteria: CriterionResponse[]
}

/** A group as a whole-set save sends it (Story criteria): no ids, the server makes new ones. */
export interface CriteriaGroupInput {
  isAnd: boolean
  orderIndex: number
  criteria: { conditionType: number; config: Record<string, unknown>; priority: number }[]
}

export interface CriteriaGroupCreateRequest {
  isAnd: boolean
}

export interface CriteriaGroupUpdateRequest {
  isAnd: boolean
}

export interface CriteriaGroupResponse {
  id: string
  isAnd: boolean
  orderIndex: number
}

export interface AdventureLookupItem {
  id: string
  title: string
  userRelationType: number
  /** One line, for a picker that shows a subtitle. */
  quickDescription?: string | null
  /** The stored path, exactly as AdventureDto.thumbnailUrl carries it. */
  thumbnailUrl?: string | null
}

export const adventuresApi = {
  // Get list of adventures with filters and pagination
  getAdventures: (params?: AdventureListParams) =>
    ApiService.get<AdventureListResponse>('/api/adventures', params),

  /**
   * The caller's adventures as a pick-list: id, title, relation flags.
   *
   * What getAdventures is for when all you need is a name to choose. That one answers with the full
   * ninety-five-field DTO -- 7 MB for an account holding the bulk imports, a megabyte of it
   * fullDescription -- while the database work behind it is 68 ms. Prefer this anywhere the user is
   * picking an adventure rather than reading one.
   */
  getMyAdventureLookup: (params?: { q?: string; take?: number }) =>
    ApiService.get<AdventureLookupItem[]>('/api/adventures/my/lookup', params),

  // Search adventures with advanced filters
  searchAdventures: (searchRequest: AdventureSearchRequest) =>
    ApiService.post<AdventureListResponse>('/api/adventures/search', searchRequest),

  // Base Camp's "Trending now": the most-played adventures in the last day the caller may see,
  // leaning towards what they have completed lately. Check-in adventures never count.
  getTrending: (params: { userLatitude?: number | null; userLongitude?: number | null; radiusMiles?: number | null; take?: number }) =>
    ApiService.get<TrendingAdventuresResponse>(
      '/api/adventures/trending',
      Object.fromEntries(Object.entries(params).filter(([, v]) => v !== null && v !== undefined))
    ),

  // Ask to be notified when an upcoming adventure opens. Idempotent. leadTimeMinutes
  // is how far ahead to warn: 0 = at open, 1440 = a day before, 10080 = a week.
  watchAdventure: (id: string, leadTimeMinutes = 0) =>
    ApiService.post<void>(`/api/adventures/${id}/watch`, { leadTimeMinutes }),

  // Stop being notified. Idempotent — succeeds even with no watch on record.
  unwatchAdventure: (id: string) =>
    ApiService.delete<void>(`/api/adventures/${id}/watch`),

  // Mark an adventure as one you want to do. Unlike a watch, this has no expiry —
  // a watch is spent when the adventure opens; a favourite is a standing note.
  favoriteAdventure: (id: string) =>
    ApiService.post<void>(`/api/adventures/${id}/favorite`),

  // Idempotent — succeeds even when it was not favourited.
  unfavoriteAdventure: (id: string) =>
    ApiService.delete<void>(`/api/adventures/${id}/favorite`),

  // The caller's own favourites, newest first. There is no endpoint for anyone else's.
  getMyFavoriteAdventures: () =>
    ApiService.get<AdventureDto[]>('/api/adventures/my/favorites'),

  // Get user's created silent adventures
  getMySilentAdventures: () =>
    ApiService.get<AdventureDto[]>('/api/adventures/my/silent'),

  // Get single adventure by ID (for viewing)
  // The viewer's live position, when known, so join criteria are judged from where they stand
  // rather than the server's stored last-known position (or their home).
  getAdventure: (id: string, viewer?: { latitude: number; longitude: number } | null) =>
    ApiService.get<AdventureDto>(
      `/api/adventures/${id}`,
      viewer ? { latitude: viewer.latitude, longitude: viewer.longitude } : undefined,
    ),

  // Get adventure for editing (includes all editor fields)
  getAdventureForEdit: (id: string) =>
    ApiService.get<AdventureDto>(`/api/adventures/${id}/edit`),

  // The adventures sitting inside this one — a park's trails. Filtered server-side
  // to what the caller may see, so the list never has to be trimmed here.
  getChildAdventures: (id: string) =>
    ApiService.get<AdventureChildDto[]>(`/api/adventures/${id}/children`),

  // Every route belonging to the adventures inside this one — a park's trails, on
  // one map. One request rather than one per child: a park here holds up to 56
  // trails, and the export endpoint would be 56 round trips to draw a single map.
  //
  // Doubly filtered server-side. Children run through the same visibility rule the
  // Inside tab uses, and each route through its own export disclosure, so a child
  // whose route is withheld is simply absent from `children` rather than present
  // and empty. Nothing here reconstructs what was left out.
  getChildRoutes: (id: string) =>
    ApiService.get<ChildRoutesResponse>(`/api/adventures/${id}/children/routes`),

  // Which of a park's trails meet each other, read out of the stored geometry on
  // demand. Answers "where does this trail go next" without 56 round trips.
  //
  // Not visibility-filtered by the server, so callers pair it with the park's
  // children list and show only trails that list names.
  getTrailNetwork: (parkId: string) =>
    ApiService.get<TrailNetworkResponse>(`/api/adventures/${parkId}/trail-network`),

  // The shortest walk from one of a park's trails to another, as a chain of legs,
  // optionally by way of others.
  //
  // A leg frequently covers only part of its trail — `enterFraction`/`exitFraction`
  // say which part, and ignoring them overstates the distance.
  //
  // `via` is ordered, and the order is walked. The server plans each pair in turn
  // and never reorders the stops to shorten the whole: somebody who asks for the
  // lake before the summit wants the lake first. A trail passed *through* comes
  // back as one leg spanning the way in to the way out, not as two legs, so a plan
  // through B still names B once.
  planTrailWalk: (
    parkId: string,
    fromTrailId: string,
    toTrailId: string,
    viaTrailIds: readonly string[] = []
  ) =>
    ApiService.get<TrailWalkPlanResponse>(
      `/api/adventures/${parkId}/trail-network/plan?from=${fromTrailId}&to=${toTrailId}` +
        (viaTrailIds.length > 0 ? `&via=${viaTrailIds.join(',')}` : '')
    ),

  // The short code behind scan.hadv.quest/<code>. Created on first request and
  // reused after, so this is safe to call every time somebody shares.
  getShareLinkCode: (id: string) =>
    ApiService.get<{ code: string }>(`/api/adventures/${id}/share-link`),

  // Re-check join eligibility at a live location (used when a player reaches a
  // geofenced start, to flip the UI to Play without a manual refresh).
  validateJoinEligibility: (id: string, location: { latitude: number; longitude: number }) =>
    ApiService.post<JoinEligibilityResult>(`/api/adventures/${id}/validate-join`, location),

  // Create new adventure
  createAdventure: (data: CreateAdventureRequest) =>
    ApiService.post<ApiResponse<AdventureDto>>('/api/adventures', data),

  // Update existing adventure
  updateAdventure: (id: string, data: UpdateAdventureRequest) =>
    ApiService.put<ApiResponse<AdventureDto>>(`/api/adventures/${id}`, data),

  // Only the named settings; the server keeps every other field as saved. What the editor saves
  // with, so two people editing different settings no longer undo each other.
  patchAdventure: (id: string, fields: Record<string, unknown>) =>
    ApiService.patch<ApiResponse<AdventureDto>>(`/api/adventures/${id}`, fields),

  // File an adventure into a folder, or unfile it with null.
  //
  // The folder belongs to the adventure's owner, not to the caller, so this changes what
  // every other maintainer sees too. The folder must be in that same owner's drive.
  setAdventureFolder: (id: string, folderId: string | null) =>
    ApiService.put<ApiResponse<void>>(`/api/adventures/${id}/folder`, { folderId }),

  // Delete adventure
  deleteAdventure: (id: string) =>
    ApiService.delete<ApiResponse<void>>(`/api/adventures/${id}`),

  // Publish adventure. For an adventure with a flow this is the versioned publish: the server
  // compiles the latest draft if needed, moves it through testing when nothing is live yet, and
  // records it as a version with publisher + settings snapshot. Without a flow it is a status change.
  // 400 carries { message, errors? } (compile errors, archived, …).
  // A trail whose route changed answers 202 with awaitingApproval: nothing went live, it waits for a moderator.
  publishAdventure: (id: string, request?: PublishAdventureRequest) =>
    ApiService.post<{ id?: string; awaitingApproval?: boolean; message?: string } | undefined>(
      `/api/adventures/${id}/workflow/publish`,
      request
    ),

  // Whether a play started now would count toward stats (one counted visit per 24 hours), and when
  // it will count again. A forecast: the server decides for real at the finish.
  getStatsCount: (id: string) =>
    ApiService.get<StatsCountStatus>(`/api/adventures/${id}/stats-count`),

  // Join adventure solo (explicit). methodUsed is the player's chosen navigation
  // method for multi-method trails (ignored server-side otherwise).
  //
  // reviewMode (Backlog 058) is "Review (test play)": for a Moderator/Admin the server forces a
  // test session and skips visibility, join criteria, dates, caps and subscription. Anyone else's
  // flag is ignored. Only sent when true, so ordinary joins are byte-for-byte unchanged.
  joinAdventureSolo: (id: string, startDateTime?: string, methodUsed?: string, reviewMode?: boolean) =>
    ApiService.post<AdventureJoinResponse>(`/api/adventures/${id}/join/solo`, {
      startDateTime,
      methodUsed,
      ...(reviewMode ? { reviewMode: true } : {}),
    }),

  // Create group and join adventure.
  // The route is join/create-session, not join/create-group — there has never been a
  // create-group route, so the "Create Group" dialog's submit had never once succeeded. It
  // 400s rather than 404s, because "create-group" binds as the {id} segment of another route.
  createGroupJoin: (id: string, request: Omit<AdventureJoinRequest, 'adventureId'>) =>
    ApiService.post<AdventureJoinResponse>(`/api/adventures/${id}/join/create-session`, request),

  // Join with existing group
  joinWithGroup: (id: string, request: Omit<AdventureJoinRequest, 'adventureId'>) =>
    ApiService.post<AdventureJoinResponse>(`/api/adventures/${id}/join/group`, request),

  // Join public queue
  joinPublic: (id: string, request: Omit<AdventureJoinRequest, 'adventureId'>) =>
    ApiService.post<AdventureJoinResponse>(`/api/adventures/${id}/join/public`, request),

  // Create a new session (invite-only or public)
  createSession: (id: string, request: Omit<AdventureJoinRequest, 'adventureId'>) =>
    ApiService.post<AdventureJoinResponse>(`/api/adventures/${id}/join/create-session`, request),

  // ----- Interest: wanting to do this with a group -----

  /**
   * This caller's interest state and the public count.
   *
   * Safe to call signed out — the count is free and anonymous, because it is the best-placed
   * upsell on the page and gating it costs the reason somebody signs up.
   */
  getInterest: (id: string) =>
    ApiService.get<AdventureInterestState>(`/api/adventures/${id}/interest`),

  /**
   * Flag interest, or re-affirm it.
   *
   * Idempotent on purpose: flagging something already flagged restarts the 90-day lapse clock,
   * which is exactly what the interest list's "still interested" action needs. Refused with 402
   * and a `subscription_required` code when the caller has no live subscription.
   */
  flagInterest: (id: string) =>
    ApiService.post<void>(`/api/adventures/${id}/interest`),

  /** Stop wanting a group. Records the reason, which is what stops us offering it back. */
  unflagInterest: (id: string) =>
    ApiService.delete<void>(`/api/adventures/${id}/interest`),

  /**
   * Say when you could go — the "when?" that follows the flag.
   *
   * Its own call rather than a body on `flagInterest`: flagging is one click and stays one click,
   * and this is a separate, skippable step. Sending it for an adventure that was never flagged
   * flags it, so the two cannot race. An empty answer means "any day, any time" and still counts
   * as answered, which is what stops the prompt.
   *
   * Does not restart the 90-day lapse clock — only an explicit "still interested" does.
   */
  setInterestAvailability: (id: string, availability: InterestAvailability) =>
    ApiService.put<void>(`/api/adventures/${id}/interest/availability`, {
      days: availability.days,
      dayparts: availability.dayparts,
      timeZoneId: availability.timeZoneId ?? null,
    }),

  // Get public sessions for an adventure
  getPublicSessions: (id: string) =>
    ApiService.get<PublicQueueSessionDto[]>(`/api/adventures/${id}/public-sessions`),

  // Join an existing public session
  joinPublicSession: (sessionId: string) =>
    ApiService.post<AdventureJoinResponse>(`/api/adventures/sessions/${sessionId}/join`),

  // Leave an adventure session
  leaveAdventureSession: (sessionId: string) =>
    ApiService.delete<ApiResponse<void>>(`/api/adventures/sessions/${sessionId}/leave`),

  // Leave a queue
  leaveQueue: (queueId: string) =>
    ApiService.delete<ApiResponse<void>>(`/api/adventures/queues/${queueId}/leave`),

  // Abandon adventure (leave/quit)
  abandonAdventure: (id: string) =>
    ApiService.post<ApiResponse<void>>(`/api/adventures/${id}/abandon`),

  // Complete adventure
  completeAdventure: (id: string) =>
    ApiService.post<ApiResponse<void>>(`/api/adventures/${id}/complete`),

  // Save adventure tags
  saveTags: (id: string, tagIds: string[]) =>
    ApiService.post<ApiResponse<void>>(`/api/adventures/${id}/tags`, tagIds),

  // Team Management - Maintainers
  getMaintainers: (id: string) =>
    ApiService.get<AdventureMaintainerDto[]>(`/api/adventures/${id}/maintainers`),
  addMaintainer: (id: string, data: AddTeamMemberRequest) =>
    ApiService.post<ApiResponse<void>>(`/api/adventures/${id}/maintainers`, data),
  removeMaintainer: (id: string, userId: string) =>
    ApiService.delete<ApiResponse<void>>(`/api/adventures/${id}/maintainers/${userId}`),

  // Team Management - Moderators
  getModerators: (id: string) =>
    ApiService.get<AdventureModeratorDto[]>(`/api/adventures/${id}/moderators`),
  addModerator: (id: string, data: AddTeamMemberRequest) =>
    ApiService.post<ApiResponse<void>>(`/api/adventures/${id}/moderators`, data),
  removeModerator: (id: string, userId: string) =>
    ApiService.delete<ApiResponse<void>>(`/api/adventures/${id}/moderators/${userId}`),

  // Team Management - Testers
  getTesters: (id: string) =>
    ApiService.get<AdventureTesterDto[]>(`/api/adventures/${id}/testers`),
  addTester: (id: string, data: AddTeamMemberRequest) =>
    ApiService.post<ApiResponse<void>>(`/api/adventures/${id}/testers`, data),
  removeTester: (id: string, userId: string) =>
    ApiService.delete<ApiResponse<void>>(`/api/adventures/${id}/testers/${userId}`),
  // Saved Filters Management
  getSavedFilters: () =>
    ApiService.get<SavedSearchFilterDto[]>('/api/Adventures/search/filters'),

  getSavedFilter: (id: string) =>
    ApiService.get<SavedSearchFilterDto>(`/api/Adventures/search/filters/${id}`),

  createSavedFilter: (data: CreateSavedSearchFilterRequest) =>
    ApiService.post<SavedSearchFilterDto>('/api/Adventures/search/filters', data),

  updateSavedFilter: (id: string, data: CreateSavedSearchFilterRequest) =>
    ApiService.put<SavedSearchFilterDto>(`/api/Adventures/search/filters/${id}`, data),

  deleteSavedFilter: (id: string) =>
    ApiService.delete<void>(`/api/Adventures/search/filters/${id}`),

  setDefaultFilter: (id: string) =>
    ApiService.post<void>(`/api/Adventures/search/filters/${id}/set-default`),

  // Get adventure categories
  getCategories: () =>
    ApiService.get<AdventureCategoryDto[]>('/api/adventures/categories'),

  // Get criteria types
  getCriteriaTypes: () =>
    ApiService.get<CriteriaTypeDto[]>('/api/adventures/criteriatypes'),

  // Get node types
  getNodeTypes: () =>
    ApiService.get<NodeTypeDto[]>('/api/adventures/nodetypes'),

  // Image uploads
  uploadThumbnail: (id: string, file: File) => {
    const formData = new FormData()
    formData.append('file', file)
    return ApiService.upload<ApiResponse<{ url: string }>>(`/api/adventures/${id}/thumbnail`, formData)
  },

  uploadCover: (id: string, file: File) => {
    const formData = new FormData()
    formData.append('file', file)
    return ApiService.upload<ApiResponse<{ url: string }>>(`/api/adventures/${id}/cover`, formData)
  },

  // Criteria - Get available criteria types for a template
  // TODO: Replace with actual API call when backend is ready
  getAvailableCriteria: async (_templateType: number) => {
    // Placeholder: Return all available criteria types until API is ready
    // In the future, this will call: /api/templates/${templateType}/available-criteria
    const { AVAILABLE_CRITERIA_TYPES } = await import('@features/adventures/types/adventure-editor.types')
    return { criteriaTypes: AVAILABLE_CRITERIA_TYPES }
  },

  // ========================
  // Display Criteria
  // ========================

  // Get display criteria for an adventure (returns groups with nested criteria)
  getDisplayCriteria: (id: string) =>
    ApiService.get<CriteriaGroupDto[]>(`/api/adventures/${id}/display-criteria`),

  // Add a display criterion
  addDisplayCriterion: (id: string, data: AddCriterionRequest) =>
    ApiService.post<CriterionResponse>(`/api/adventures/${id}/display-criteria/add`, data),

  // Update a display criterion
  updateDisplayCriterion: (id: string, criterionId: string, data: UpdateCriterionRequest) =>
    ApiService.put<CriterionResponse>(`/api/adventures/${id}/display-criteria/${criterionId}`, data),

  // Delete a display criterion
  deleteDisplayCriterion: (id: string, criterionId: string) =>
    ApiService.delete<boolean>(`/api/adventures/${id}/display-criteria/${criterionId}`),

  // Display Criteria Logic Groups
  createDisplayCriteriaGroup: (id: string, data: CriteriaGroupCreateRequest) =>
    ApiService.post<CriteriaGroupResponse>(`/api/adventures/${id}/display-criteria/groups`, data),

  updateDisplayCriteriaGroup: (id: string, groupId: string, data: CriteriaGroupUpdateRequest) =>
    ApiService.put<CriteriaGroupResponse>(`/api/adventures/${id}/display-criteria/groups/${groupId}`, data),

  deleteDisplayCriteriaGroup: (id: string, groupId: string) =>
    ApiService.delete<boolean>(`/api/adventures/${id}/display-criteria/groups/${groupId}`),

  // ========================
  // Join Criteria
  // ========================

  // Get join criteria for an adventure (returns groups with nested criteria)
  getJoinCriteria: (id: string) =>
    ApiService.get<CriteriaGroupDto[]>(`/api/adventures/${id}/join-criteria`),

  // Add a join criterion
  addJoinCriterion: (id: string, data: AddCriterionRequest) =>
    ApiService.post<CriterionResponse>(`/api/adventures/${id}/join-criteria/add`, data),

  // Update a join criterion
  updateJoinCriterion: (id: string, criterionId: string, data: UpdateCriterionRequest) =>
    ApiService.put<CriterionResponse>(`/api/adventures/${id}/join-criteria/${criterionId}`, data),

  // Delete a join criterion
  deleteJoinCriterion: (id: string, criterionId: string) =>
    ApiService.delete<boolean>(`/api/adventures/${id}/join-criteria/${criterionId}`),

  // Join Criteria Logic Groups
  createJoinCriteriaGroup: (id: string, data: CriteriaGroupCreateRequest) =>
    ApiService.post<CriteriaGroupResponse>(`/api/adventures/${id}/join-criteria/groups`, data),

  updateJoinCriteriaGroup: (id: string, groupId: string, data: CriteriaGroupUpdateRequest) =>
    ApiService.put<CriteriaGroupResponse>(`/api/adventures/${id}/join-criteria/groups/${groupId}`, data),

  deleteJoinCriteriaGroup: (id: string, groupId: string) =>
    ApiService.delete<boolean>(`/api/adventures/${id}/join-criteria/groups/${groupId}`),

  // ========================
  // Use Display for Joining
  // ========================

  // Set whether to use display criteria for joining
  setUseDisplayForJoining: (id: string, useDisplayForJoining: boolean) =>
    ApiService.put<boolean>(`/api/adventures/${id}/use-display-for-joining`, { useDisplayForJoining }),

  // ========================
  // Moderator Status
  // ========================

  // Get moderator online status for an adventure
  getModeratorStatus: (id: string) =>
    ApiService.get<ModeratorStatusDto>(`/api/adventures/${id}/moderator-status`),

  // ========================
  // Leaderboard (for Trail adventures)
  // ========================

  // Get completion time leaderboard for an adventure
  getLeaderboard: (id: string, params?: LeaderboardQueryParams) =>
    ApiService.get<AdventureLeaderboardResponse>(`/api/adventures/${id}/leaderboard`, params),

  // ========================
  // GPX Export
  // ========================

  // Get routes data for GPX export (respects disclosure rules based on session progress)
  getRoutesForExport: (id: string, sessionId?: string) =>
    ApiService.get<GpxExportData>(`/api/adventures/${id}/routes/export`, sessionId ? { sessionId } : undefined),

  // ========================
  // Completed Adventures (Profile Adventures tab)
  // ========================

  // Current user's completed adventures (owner view: includes visibility override fields)
  // `groupRepeats` folds repeat completions of one adventure into one row (playCount/firstCompletedAt).
  getMyCompleted: (page = 1, pageSize = 20, groupRepeats?: boolean) =>
    ApiService.get<CompletedAdventuresResponse>('/api/me/adventures/completed', { page, pageSize, groupRepeats }),

  // Another user's completed adventures (server-filtered; restricted rows are title-only)
  getUserCompleted: (userId: string, page = 1, pageSize = 20, groupRepeats?: boolean) =>
    ApiService.get<CompletedAdventuresResponse>(`/api/users/${userId}/adventures/completed`, { page, pageSize, groupRepeats }),

  // Set (or clear) the per-session visibility override for a completed adventure.
  // Reuses the existing activity visibility path. `visibility` is INT 0-4; null clears -> use global.
  setAdventureVisibility: (sessionId: string, visibility: number | null) =>
    ApiService.put<unknown>(
      `/api/me/activity/Adventure/${sessionId}/visibility`,
      { visibility }
    ),
}

// Publish types
export interface PublishAdventureRequest {
  /** Audit reason for the status change. */
  reason?: string
  /** "What changed", shown to players for this version. */
  description?: string
  /**
   * The creator's explicit answer on whether the version changes how hard it is to score well.
   * Omit (or null) to let the server's detector decide — never default this to false, which the
   * server reads as the creator overriding the detector.
   */
  isScoringSignificant?: boolean | null
  /** Why, when isScoringSignificant is an explicit answer. */
  significanceReason?: string
}

// GPX Export types
export interface GpxRouteExportDto {
  id: string
  label: string
  travelMode: string
  // Descriptive surface/terrain for this section ('dirt', 'gravel', …; a
  // RouteSurfaceType). Optional — a creator can leave a section's surface blank,
  // and blank means unrecorded rather than any particular ground.
  surfaceType?: string
  // Array of [lat, lng] or [lat, lng, elevationMeters] — the backend stores
  // section points as a flexible JSON double[], so recorded elevation rides along.
  routePoints: ([number, number] | [number, number, number])[]
  distanceMeters?: number
  durationSeconds?: number
}

export interface GpxWaypointExportDto {
  id: string
  name: string
  description?: string
  latitude: number
  longitude: number
  // Elevation in meters. Populated by the backend from the node point's
  // altitude; emitted as <ele> in the exported GPX when present.
  altitude?: number
  type: 'start' | 'end' | 'location'
}

// ---------------------------------------------------------------------------
// Child routes — the trails inside a park
// ---------------------------------------------------------------------------

/**
 * One styled stretch of a child's route.
 *
 * Unlike {@link GpxRouteExportDto} this carries the *authored* styling. That
 * distinction matters: the GPX shape has no room for colour or line style, so the
 * single-trail views have to infer a return leg from the geometry, while here the
 * import's own choice — a second colour and a dashed line for the way back —
 * arrives intact and should simply be drawn.
 */
export interface ChildRouteSectionDto {
  sectionId: string
  name: string
  order: number
  // [lat, lng] or [lat, lng, elevationMeters] — stored as a loose JSON double[],
  // so altitude rides along only where the geometry was captured with it.
  routePoints: number[][]
  travelMode: string
  surfaceType?: string | null
  color: string
  lineStyle: string
  lineOpacity: number
}

/** One child adventure and the line it asks you to walk. */
export interface ChildRouteDto {
  adventureId: string
  title: string
  thumbnailUrl?: string | null
  /** Metres, as walked — an out-and-back counts the whole trip. */
  distanceMeters: number
  sections: ChildRouteSectionDto[]
}

export interface ChildRoutesResponse {
  adventureId: string
  adventureTitle: string
  /** The children with a route this caller may see. Others are absent, not empty. */
  children: ChildRouteDto[]
}

// ---------------------------------------------------------------------------
// Trail network — which of a park's trails meet, and how to walk between them
// ---------------------------------------------------------------------------

/** One trail in a park's network. */
export interface TrailNodeDto {
  adventureId: string
  title: string
  /** Metres, as walked — an out-and-back counts the whole trip. */
  distanceMeters: number
  startLatitude: number
  startLongitude: number
  endLatitude: number
  endLongitude: number
}

/**
 * Two trails that meet, and where along each.
 *
 * The fractions are positions along a trail's line — 0 at its start, 1 at its
 * end — and they are the whole point of this payload. Nearly half of the
 * junctions in a real park are end-to-middle (one trail finishing partway along
 * another), so a reading that only knew about endpoints would describe a
 * different park from the one that exists.
 */
export interface TrailJunctionDto {
  fromAdventureId: string
  toAdventureId: string
  /** Where along the `from` trail. The server only emits its ends, so 0 or 1. */
  fromFraction: number
  /** Where along the `to` trail — anywhere between 0 and 1. */
  toFraction: number
  /** How far apart the two lines actually are here. Zero when they share a point. */
  gapMeters: number
  latitude: number
  longitude: number
}

/**
 * A park's trails and every place they come within reach of each other.
 *
 * Not filtered by visibility server-side — unlike `children/routes`, which runs
 * children through the same rule the Inside tab uses. Callers here narrow it to
 * the children the viewer may see before showing anything; see
 * `useVisibleChildIds`.
 */
export interface TrailNetworkResponse {
  parkId: string
  parkTitle: string
  trails: TrailNodeDto[]
  junctions: TrailJunctionDto[]
}

/** One trail within a planned walk — often only part of it. */
export interface TrailWalkLegDto {
  order: number
  adventureId: string
  title: string
  /** The whole trail's length. What this leg covers is the fraction span of it. */
  distanceMeters: number
  /** Where you join this trail, and where you leave it. 0 is its start, 1 its end. */
  enterFraction: number
  exitFraction: number
  /** The step across to the next leg, when the two lines do not quite touch. */
  gapToNextMeters: number
}

/** A walk made of several trails. */
export interface TrailWalkPlanResponse {
  found: boolean
  /** Why not, when `found` is false — written plainly enough to show a walker. */
  message?: string | null
  legs: TrailWalkLegDto[]
  /** The sum of the legs *and* the gaps between them, in metres. */
  totalDistanceMeters: number
}

export interface GpxExportData {
  adventureName: string
  adventureDescription?: string
  creatorName?: string
  routes: GpxRouteExportDto[]
  waypoints: GpxWaypointExportDto[]
  exportEnabled: boolean
}
